import { BadgeIcon, LicenseIcon, UpliftUIcon } from '@uplift/icons';
import { CollapsableBulletList, SectionContainer, SubSectionContainer } from '../components';
import { Business, SchoolOutlined } from '@mui/icons-material';
import { useSelectedProviderContext } from '../selected-provider.context';
import { Collapse, ListItemText, Typography } from '@mui/material';
import { useSelectedProviderStyles } from '../styles';
import { getFormattedAddressParts } from '@uplift/utils';
import { useMemo } from 'react';
import { CalendarDateService } from '@uplift/services';
import { SelectedProviderSectionProps, SelectedProviderSubSectionEnum, SelectedProviderTabEnum } from '../types';
import { useSelectedProviderFieldTransitionConfigs } from '../helpers';
import { ProviderMatchingProfileBookmark } from '@uplift/api';

const getTimeOnUplift = (createdAt: string) => {
  const joinedOn = CalendarDateService.date(createdAt);
  const months = CalendarDateService.date().diff(joinedOn, 'month');

  if (months === 0) {
    return '< 1 month';
  }

  const years = Math.floor(months / 12);
  const remainingMonths = months % 12;

  const yearsDisplay = `${years} year${years > 1 ? 's' : ''}`;
  const monthsDisplay = `${remainingMonths} month${remainingMonths > 1 ? 's' : ''}`;

  if (years > 0) {
    if (remainingMonths === 0) {
      return yearsDisplay;
    }
    return `${yearsDisplay}, ${monthsDisplay}`;
  } else {
    return monthsDisplay;
  }
};

export type MoreInfoSectionProps = SelectedProviderSectionProps & {
  hideLicenses?: boolean;
};

export const MoreInfoSection: React.VFC<MoreInfoSectionProps> = props => {
  const { getTransitionConfig } = useSelectedProviderFieldTransitionConfigs(props.fieldTransitions);
  const { classes } = useSelectedProviderStyles();
  const { selectedProvider } = useSelectedProviderContext();

  const timeOnUplift = useMemo(() => getTimeOnUplift(selectedProvider.createdAt), [ selectedProvider.createdAt ]);

  const educationTransitionConfig = getTransitionConfig(ProviderMatchingProfileBookmark.qualification);
  const addressTransitionConfig = getTransitionConfig(ProviderMatchingProfileBookmark.locations);

  return (
    <SectionContainer
      title="More Info"
      id={SelectedProviderTabEnum.moreInfo}
      forceMobile={props.forceMobile}
    >
      <SubSectionContainer
        title="Licenses:"
        icon={LicenseIcon}
        hidden={props.hideLicenses}
        id={SelectedProviderSubSectionEnum.licenses}
        forceMobile={props.forceMobile}
      >
        <CollapsableBulletList
          listItems={selectedProvider.userProfile.license.map((license) => {

            return (
              <ListItemText
                key={license.id}
                primary={license.name}
                secondary={license.state?.fullName}
                classes={{
                  root: classes.listItemText,
                  secondary: classes.fontSize16
                }}
              />
            );
          })}
        />
      </SubSectionContainer>
      <SubSectionContainer
        title="Education:"
        icon={SchoolOutlined}
        id={SelectedProviderSubSectionEnum.education}
        forceMobile={props.forceMobile}
        in={educationTransitionConfig.shown}
        highlight={educationTransitionConfig.active}
      >
        <Collapse in={educationTransitionConfig.transitionIn}>
          <CollapsableBulletList
            listItems={selectedProvider.qualification.degrees.map((degree) => {

              return (
                <ListItemText
                  key={degree.id}
                  primary={`${degree.degree} in ${degree.fieldOfStudy}`}
                  secondary={degree.school}
                  classes={{
                    root: classes.listItemText,
                    secondary: classes.fontSize16
                  }}
                />
              );
            })}
          />
        </Collapse>
      </SubSectionContainer>
      <SubSectionContainer
        title="Office Location:"
        icon={Business}
        id={SelectedProviderSubSectionEnum.practiceAddress}
        forceMobile={props.forceMobile}
        in={addressTransitionConfig.shown}
        highlight={addressTransitionConfig.active}
      >
        <Collapse in={addressTransitionConfig.transitionIn}>
          <CollapsableBulletList
            listItems={selectedProvider.userProfile.practice?.locations.map((address) => {
              const addressParts = getFormattedAddressParts({
                address1: address.addressLine1 ?? '',
                address2: address.addressLine2 ?? '',
                city: address.city ?? '',
                zip: address.zip ?? '',
              }, address.state, {
                stateName: 'shortName'
              });

              return address.hideStreetAddress ? addressParts.addressCityState : addressParts.fullAddress;
            }) ?? []}
          />
        </Collapse>
      </SubSectionContainer>
      <SubSectionContainer
        title="Time on UpLift:"
        icon={UpliftUIcon}
        contentInline
        id={SelectedProviderSubSectionEnum.timeOnUplift}
        forceMobile={props.forceMobile}
      >
        <Typography component="span">
          {timeOnUplift}
        </Typography>
      </SubSectionContainer>
      <SubSectionContainer
        title="NPI:"
        icon={BadgeIcon}
        contentInline
        id={SelectedProviderSubSectionEnum.npi}
        forceMobile={props.forceMobile}
      >
        <Typography component="span">
          {selectedProvider.npi}
        </Typography>
      </SubSectionContainer>
    </SectionContainer>
  );
};
